import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authenticatedFetch } from '../utils/auth';

// Modal Component for CSV upload
const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '1px solid #dee2e6',
                    paddingBottom: '16px'
                }}>
                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>{title}</h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#666',
                            padding: '0',
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        ×
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

const MyLocationsPage = () => {
    const [addresses, setAddresses] = useState([]);
    const [addressLoading, setAddressLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    // CSV upload states
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [uploadMode, setUploadMode] = useState('append');
    const [csvLoading, setCsvLoading] = useState(false);
    
    // Add location form modal (replacing instructions modal)
    const [showAddLocationModal, setShowAddLocationModal] = useState(false);
    
    // Form states for add location
    const [formData, setFormData] = useState({
        label: '',
        address: '',
        coordinates: '', // lat,lng format
        setAsDefault: false
    });
    const [formLoading, setFormLoading] = useState(false);
    const [formMessage, setFormMessage] = useState('');
    
    // Default location states
    const [defaultSavedLocation, setDefaultSavedLocation] = useState(null);
    
    // Loading states for different operations
    const [deleteLoading, setDeleteLoading] = useState({}); // {addressId: true/false}
    const [setDefaultLoading, setSetDefaultLoading] = useState({}); // {addressId: true/false}
    const [operationLoading, setOperationLoading] = useState(false);

    // Fetch addresses on component mount
    useEffect(() => {
        const initializePage = async () => {
            setPageLoading(true);
            try {
                await Promise.all([
                    fetchAddresses(),
                    fetchDefaultLocation()
                ]);
            } catch (error) {
                console.error('Error initializing page:', error);
            } finally {
                setPageLoading(false);
            }
        };
        
        initializePage();
    }, []);

    const fetchAddresses = async () => {
        setAddressLoading(true);
        try {
            const response = await authenticatedFetch('/addresses');
            if (response.ok) {
                const data = await response.json();
                setAddresses(data);
            } else {
                setMessage('Error loading addresses.');
            }
        } catch (error) {
            setMessage('Error loading addresses.');
        } finally {
            setAddressLoading(false);
        }
    };

    const fetchDefaultLocation = async () => {
        try {
            const response = await authenticatedFetch('/addresses/default');
            if (response.ok) {
                const data = await response.json();
                setDefaultSavedLocation(data.default_address);
            } else {
                setDefaultSavedLocation(null);
            }
        } catch (error) {
            console.error('Error fetching default location:', error);
            setDefaultSavedLocation(null);
        }
    };

    const setDefaultSavedLocationFunction = async (addressId) => {
        setSetDefaultLoading(prev => ({ ...prev, [addressId]: true }));
        setOperationLoading(true);
        try {
            // Check if this address is already the default
            const isCurrentlyDefault = defaultSavedLocation?.id === addressId;
            
            if (isCurrentlyDefault) {
                // Unset the default location
                const response = await authenticatedFetch('/addresses/unset-default', {
                    method: 'DELETE',
                });
                if (response.ok) {
                    await fetchDefaultLocation();
                    setMessage('Default location removed successfully');
                    // Clear the address cache to ensure fresh data on next app load
                    localStorage.removeItem('digipin_addresses_cache');
                    localStorage.removeItem('digipin_addresses_timestamp');
                } else {
                    setMessage('Error removing default location.');
                }
            } else {
                // Set as default location
                const response = await authenticatedFetch('/addresses/set-default', {
                    method: 'POST',
                    body: JSON.stringify({ address_id: addressId }),
                });
                if (response.ok) {
                    await fetchDefaultLocation();
                    setMessage('Default location updated successfully');
                    // Clear the address cache to ensure fresh data on next app load
                    localStorage.removeItem('digipin_addresses_cache');
                    localStorage.removeItem('digipin_addresses_timestamp');
                } else {
                    setMessage('Error setting default location.');
                }
            }
        } catch (error) {
            const isCurrentlyDefault = defaultSavedLocation?.id === addressId;
            setMessage(isCurrentlyDefault ? 'Error removing default location.' : 'Error setting default location.');
        } finally {
            setSetDefaultLoading(prev => ({ ...prev, [addressId]: false }));
            setOperationLoading(false);
        }
    };

    const handleDeleteAddress = async (addressId) => {
        if (!window.confirm('Are you sure you want to delete this location?')) {
            return;
        }

        setDeleteLoading(prev => ({ ...prev, [addressId]: true }));
        setOperationLoading(true);
        try {
            const response = await authenticatedFetch(`/addresses/${addressId}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                setMessage('Location deleted successfully');
                await fetchAddresses();
                // Also update the AddressContext cache
                const updatedResponse = await authenticatedFetch('/addresses');
                if (updatedResponse.ok) {
                    const updatedAddresses = await updatedResponse.json();
                    localStorage.setItem('digipin_addresses_cache', JSON.stringify(updatedAddresses));
                    localStorage.setItem('digipin_addresses_timestamp', Date.now().toString());
                }
            } else {
                setMessage('Error deleting location.');
            }
        } catch (error) {
            setMessage('Error deleting location.');
        } finally {
            setDeleteLoading(prev => ({ ...prev, [addressId]: false }));
            setOperationLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            label: '',
            address: '',
            coordinates: '',
            setAsDefault: false
        });
        setShowAddLocationModal(false);
        setFormMessage('');
    };

    const handleAddAddress = async (e) => {
        e.preventDefault();
        if (!formData.label.trim() || !formData.address.trim() || !formData.coordinates.trim()) {
            setFormMessage('Please provide label, address, and coordinates.');
            return;
        }

        // Validate coordinates format (should be lat,lng) - allow spaces around comma
        const coordPattern = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/;
        if (!coordPattern.test(formData.coordinates.trim())) {
            setFormMessage('Please enter coordinates in the format: latitude,longitude (e.g., 28.6139,77.2090)');
            return;
        }

        setFormLoading(true);
        setOperationLoading(true);
        setFormMessage('');

        try {
            // Parse coordinates
            const [lat, lng] = formData.coordinates.split(',').map(coord => parseFloat(coord.trim()));
            
            const response = await authenticatedFetch('/addresses', {
                method: 'POST',
                body: JSON.stringify({
                    address: formData.address,
                    label: formData.label,
                    lat: lat,
                    lng: lng,
                    input_type: 'address' // Since we're providing both address and coordinates
                }),
            });

            if (response.ok) {
                const addedAddress = await response.json();
                
                // If user wants to set this as default location
                if (formData.setAsDefault) {
                    const defaultResponse = await authenticatedFetch('/addresses/set-default', {
                        method: 'POST',
                        body: JSON.stringify({
                            address_id: addedAddress.id
                        }),
                    });
                    
                    if (defaultResponse.ok) {
                        setMessage('Location added and set as default location successfully!');
                    } else {
                        setMessage('Location added successfully, but failed to set as default location.');
                    }
                } else {
                    setMessage('Location added successfully!');
                }
                
                resetForm();
                await fetchAddresses();
                await fetchDefaultLocation(); // Refresh default location data
                
                // Also update the AddressContext cache
                const updatedResponse = await authenticatedFetch('/addresses');
                if (updatedResponse.ok) {
                    const updatedAddresses = await updatedResponse.json();
                    localStorage.setItem('digipin_addresses_cache', JSON.stringify(updatedAddresses));
                    localStorage.setItem('digipin_addresses_timestamp', Date.now().toString());
                }
            } else {
                const errorData = await response.json();
                setFormMessage(`Error: ${errorData.detail || errorData.message || 'Failed to add location.'}`);
            }
        } catch (error) {
            setFormMessage(`Error: ${error.message || 'Could not save location. Please try again.'}`);
        } finally {
            setFormLoading(false);
            setOperationLoading(false);
        }
    };

    const handleCsvUpload = async (e) => {
        e.preventDefault();
        if (!csvFile) return;

        setCsvLoading(true);
        setOperationLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', csvFile);
            formData.append('upload_mode', uploadMode);

            const response = await authenticatedFetch('/addresses/bulk-upload', {
                method: 'POST',
                body: formData,
                headers: {
                    // Don't set Content-Type, let the browser set it with boundary
                }
            });

            const data = await response.json();
            if (response.ok) {
                setMessage(data.message);
                await fetchAddresses();
                // Also update the AddressContext cache
                const updatedResponse = await authenticatedFetch('/addresses');
                if (updatedResponse.ok) {
                    const updatedAddresses = await updatedResponse.json();
                    localStorage.setItem('digipin_addresses_cache', JSON.stringify(updatedAddresses));
                    localStorage.setItem('digipin_addresses_timestamp', Date.now().toString());
                }
                setShowCsvModal(false);
                setCsvFile(null);
                setUploadMode('append');
            } else {
                setMessage(data.detail || 'Failed to upload CSV.');
            }
        } catch (error) {
            setMessage('Network error during CSV upload.');
        } finally {
            setCsvLoading(false);
            setOperationLoading(false);
        }
    };

    // Filter addresses based on search term
    const filteredAddresses = addresses.filter(addr => {
        const searchLower = searchTerm.toLowerCase();
        return (
            (addr.label && addr.label.toLowerCase().includes(searchLower)) ||
            (addr.address && addr.address.toLowerCase().includes(searchLower)) ||
            (addr.lat && addr.lat.toString().includes(searchLower)) ||
            (addr.lng && addr.lng.toString().includes(searchLower)) ||
            (addr.digipin && addr.digipin.toLowerCase().includes(searchLower)) ||
            (addr.place_id && addr.place_id.toLowerCase().includes(searchLower))
        );
    });

    // Page loading overlay
    if (pageLoading || operationLoading) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #007bff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <div style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: '#333',
                    textAlign: 'center'
                }}>
                    {pageLoading ? 'Loading My Locations...' : 'Updating...'}
                </div>
            </div>
        );
    }

    return (
        <div style={{ 
            width: '100%',
            // minHeight: '100vh',
            // backgroundColor: '#f8f9fa',
            // padding: '24px'
        }}>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>

            

            {/* Message Display */}
            {message && (
                <div style={{ 
                    marginBottom: 16, 
                    padding: 12, 
                    backgroundColor: message.includes('Error') ? '#f8d7da' : '#d4edda', 
                    color: message.includes('Error') ? '#721c24' : '#155724', 
                    borderRadius: 8, 
                    fontSize: 15 
                }}>
                    {message}
                </div>
            )}

            {/* Table Container */}
            <div style={{ 
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                {/* Search Bar and Action Buttons */}
                <div style={{ 
                    padding: '20px',
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    {/* Search Input */}
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <input
                            type="text"
                            placeholder="Search locations by label, address, coordinates, digipin, or place ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 0px',
                                border: '1px solid #ced4da',
                                borderRadius: '6px',
                                fontSize: '16px',
                                outline: 'none',
                                transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#007bff'}
                            onBlur={(e) => e.target.style.borderColor = '#ced4da'}
                        />
                    </div>
                    
                    {/* Add New Location Button */}
                    <button 
                        onClick={() => setShowAddLocationModal(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,123,255,0.2)',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#0056b3';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 3px 6px rgba(0,123,255,0.3)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = '#007bff';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,123,255,0.2)';
                        }}
                    >
                        <span style={{ fontSize: '16px' }}>📍</span>
                        Add New Location
                    </button>
                    
                    {/* Bulk Upload Button */}
                    <button 
                        onClick={() => setShowCsvModal(true)}
                        disabled={csvLoading}
                        style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            background: '#17a2b8', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '6px',
                            cursor: csvLoading ? 'not-allowed' : 'pointer',
                            opacity: csvLoading ? 0.6 : 1,
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(23,162,184,0.2)',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => {
                            if (!csvLoading) {
                                e.target.style.backgroundColor = '#138496';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 3px 6px rgba(23,162,184,0.3)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!csvLoading) {
                                e.target.style.backgroundColor = '#17a2b8';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 4px rgba(23,162,184,0.2)';
                            }
                        }}
                    >
                        <span style={{ fontSize: '16px' }}>📁</span>
                        Bulk Upload CSV
                    </button>
                </div>
                
                {/* Table */}
                <div style={{ 
                    height: 'calc(100vh - 400px)',
                    overflowY: 'auto'
                }}>
                    {addressLoading && addresses.length === 0 ? (
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            height: '200px',
                            color: '#666'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                border: '3px solid #f3f3f3',
                                borderTop: '3px solid #007bff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                marginRight: '16px'
                            }}></div>
                            Loading locations...
                        </div>
                    ) : addresses.length === 0 ? (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '60px 20px', 
                            color: '#666' 
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>No locations saved yet</h3>
                            <p style={{ margin: '0 0 20px 0', fontSize: '16px' }}>
                                Add your first location to get started!
                            </p>
                            <Link 
                                to="/pin2point" 
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px 24px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: '6px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                            >
                                🗺️ Go to Pin2Point
                            </Link>
                        </div>
                    ) : (
                        <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse'
                        }}>
                            <thead style={{ 
                                position: 'sticky', 
                                top: 0, 
                                background: '#f8f9fa', 
                                zIndex: 1,
                                borderBottom: '2px solid #dee2e6'
                            }}>
                                <tr>
                                    <th style={{ 
                                        padding: '16px 12px', 
                                        textAlign: 'left', 
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#495057',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>Label</th>
                                    <th style={{ 
                                        padding: '16px 12px', 
                                        textAlign: 'left', 
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#495057',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>Address</th>
                                    <th style={{ 
                                        padding: '16px 12px', 
                                        textAlign: 'left', 
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#495057',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>Coordinates</th>
                                    <th style={{ 
                                        padding: '16px 12px', 
                                        textAlign: 'left', 
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#495057',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>Digipin</th>
                                    <th style={{ 
                                        padding: '16px 12px', 
                                        textAlign: 'left', 
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#495057',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>Place ID</th>
                                    <th style={{ 
                                        padding: '16px 12px', 
                                        textAlign: 'center', 
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#495057',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>Default</th>
                                    <th style={{ 
                                        padding: '16px 12px', 
                                        textAlign: 'center', 
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#495057',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAddresses.map((addr) => (
                                    <tr 
                                        key={addr.id} 
                                        style={{ 
                                            borderBottom: '1px solid #f1f3f4',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                                    >
                                        <td style={{ 
                                            padding: '16px 12px', 
                                            fontWeight: '500',
                                            fontSize: '15px',
                                            color: '#212529'
                                        }}>
                                            {addr.label || addr.address}
                                        </td>
                                        <td style={{ 
                                            padding: '16px 12px', 
                                            fontSize: '14px', 
                                            color: '#666',
                                            maxWidth: '200px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {addr.address}
                                        </td>
                                        <td style={{ 
                                            padding: '16px 12px', 
                                            fontSize: '14px', 
                                            color: '#666',
                                            fontfamily: 'monospace'
                                        }}>
                                            {addr.lat}, {addr.lng}
                                        </td>
                                        <td style={{ 
                                            padding: '16px 12px', 
                                            fontSize: '14px', 
                                            color: '#666', 
                                            fontfamily: 'monospace'
                                        }}>
                                            {addr.digipin || 'N/A'}
                                        </td>
                                        <td style={{ 
                                            padding: '16px 12px', 
                                            fontSize: '14px', 
                                            color: '#666', 
                                            fontfamily: 'monospace',
                                            maxWidth: '150px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {addr.place_id || 'N/A'}
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                            {setDefaultLoading[addr.id] ? (
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    border: '2px solid #f3f3f3',
                                                    borderTop: '2px solid #007bff',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite',
                                                    margin: '0 auto'
                                                }}></div>
                                            ) : (
                                                <input
                                                    type="checkbox"
                                                    checked={defaultSavedLocation?.id === addr.id}
                                                    onChange={() => setDefaultSavedLocationFunction(addr.id)}
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        accentColor: '#007bff',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                                            <button 
                                                onClick={() => handleDeleteAddress(addr.id)}
                                                disabled={deleteLoading[addr.id]}
                                                style={{ 
                                                    background: '#dc3545', 
                                                    color: 'white', 
                                                    border: 'none', 
                                                    borderRadius: 6, 
                                                    padding: '8px 16px',
                                                    cursor: deleteLoading[addr.id] ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    opacity: deleteLoading[addr.id] ? 0.6 : 1,
                                                    transition: 'background-color 0.2s'
                                                }}
                                                onMouseOver={(e) => !deleteLoading[addr.id] && (e.target.style.backgroundColor = '#c82333')}
                                                onMouseOut={(e) => !deleteLoading[addr.id] && (e.target.style.backgroundColor = '#dc3545')}
                                            >
                                                {deleteLoading[addr.id] ? (
                                                    <div style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        border: '2px solid #ffffff',
                                                        borderTop: '2px solid transparent',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite',
                                                        margin: '0 auto'
                                                    }}></div>
                                                ) : 'Delete'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Add Location Form Modal */}
            <Modal 
                isOpen={showAddLocationModal} 
                onClose={() => setShowAddLocationModal(false)}
                title="Add New Location"
            >
                <form onSubmit={handleAddAddress}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                Label:
                            </label>
                            <input
                                type="text"
                                value={formData.label}
                                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                                placeholder="Enter a label for this location..."
                                required
                                style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                Address:
                            </label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                placeholder="Enter the address..."
                                required
                                style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                Coordinates (Latitude, Longitude):
                            </label>
                            <input
                                type="text"
                                value={formData.coordinates}
                                onChange={(e) => setFormData(prev => ({ ...prev, coordinates: e.target.value }))}
                                placeholder="e.g., 28.6139,77.2090"
                                required
                                style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.setAsDefault}
                                    onChange={(e) => setFormData(prev => ({ ...prev, setAsDefault: e.target.checked }))}
                                    style={{ 
                                        width: '16px', 
                                        height: '16px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{ fontWeight: '500', fontSize: '14px' }}>
                                    Set as default location
                                </span>
                            </label>
                        </div>
                    </div>
                    
                    {formMessage && (
                        <div style={{ 
                            marginTop: '16px', 
                            padding: '8px', 
                            backgroundColor: formMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                            border: `1px solid ${formMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
                            borderRadius: '4px',
                            color: formMessage.includes('Error') ? '#721c24' : '#155724',
                            fontSize: '14px'
                        }}>
                            {formMessage}
                        </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                        <button 
                            type="button"
                            onClick={() => setShowAddLocationModal(false)}
                            disabled={formLoading}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#6c757d', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px',
                                cursor: formLoading ? 'not-allowed' : 'pointer',
                                opacity: formLoading ? 0.6 : 1
                            }}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit"
                            disabled={formLoading || !formData.label.trim() || !formData.address.trim() || !formData.coordinates.trim()}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#007bff', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px',
                                cursor: (formLoading || !formData.label.trim() || !formData.address.trim() || !formData.coordinates.trim()) ? 'not-allowed' : 'pointer',
                                opacity: (formLoading || !formData.label.trim() || !formData.address.trim() || !formData.coordinates.trim()) ? 0.6 : 1
                            }}
                        >
                            {formLoading ? 'Adding...' : 'Add Location'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* CSV Upload Modal */}
            <Modal 
                isOpen={showCsvModal} 
                onClose={() => setShowCsvModal(false)}
                title="Bulk Upload Locations"
            >
                <form onSubmit={handleCsvUpload}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                CSV File:
                            </label>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) => setCsvFile(e.target.files[0])}
                                required
                                style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                Upload Mode:
                            </label>
                            <select
                                value={uploadMode}
                                onChange={(e) => setUploadMode(e.target.value)}
                                style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="append">Append to existing locations</option>
                                <option value="replace">Replace all locations</option>
                            </select>
                        </div>
                        
                        <div style={{ 
                            padding: '12px', 
                            background: '#f8f9fa', 
                            borderRadius: '4px', 
                            fontSize: '13px', 
                            color: '#666',
                            border: '1px solid #dee2e6'
                        }}>
                            <strong>CSV Format:</strong> Include columns for <code>label</code> and <code>address</code>. 
                            The system will automatically detect if the address is a regular address, latitude/longitude coordinates, or a digipin code.
                            <br />
                            <a 
                                href="/sample_my_locations.csv" 
                                download="sample_my_locations.csv"
                                style={{ 
                                    color: '#007bff', 
                                    textDecoration: 'underline', 
                                    fontWeight: '500',
                                    marginTop: '8px',
                                    display: 'inline-block'
                                }}
                            >
                                📥 Download Sample CSV
                            </a>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                        <button 
                            type="button"
                            onClick={() => setShowCsvModal(false)}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#6c757d', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={!csvFile || csvLoading}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#17a2b8', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px',
                                cursor: csvLoading ? 'not-allowed' : 'pointer',
                                opacity: csvLoading ? 0.6 : 1
                            }}
                        >
                            {csvLoading ? 'Uploading...' : 'Upload CSV'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default MyLocationsPage;
